// ** Third Party Components
import { Download, EllipsisVertical, FolderSearch, Mail, MoreHorizontal } from "lucide-react"

// ** UI Components
import { Button } from "@components/common/button"
import { Popover, PopoverContent, PopoverTrigger } from "@components/common/popover"

// ** Types
import { DictType } from "@pma/types"

// ** Constants
import { PRINTABLE_EXTENSIONS } from "@pma/utils/constants"

// ** Custom Components
import useTranslator from "@hooks/use-translator"
import { useFilesView } from "@pma/hooks/use-files-view"

// ** Custom Components
import CutFile from "../../cut-file"
import CopyFile from "../../copy-file"
import PrintFile from "../../print-file"
import RestoreFile from "../../restore-file"
import DeleteFile from "@components/dashboard/delete-file"
import ShareFileOnWhatsApp from "../../share-file-on-whatsapp"

interface FileContextMenuProps {
  item: DictType
  variant?: "horizontal" | "vertical"
  triggerClassName?: string
}

export default function FileContextMenu({
  item,
  variant = "horizontal",
  triggerClassName = "",
}: FileContextMenuProps) {
  const { translate } = useTranslator()
  const {
    folderItems,
    activeFolder,
    foldersMeta,
    isOrganizationOwner,
    isAdmin,
    setCutFiles,
    setCopyFiles,
    setPreFolderItems,
    setFolderId,
    setFolderItems,
    setBreadcrumbs,
    setSelectedFiles,
    setShareViaEmailModal,
    setIsMenuView,
    setSelectedMoveFile,
    setMoveFileModal,
    moveFileModal,
    setRecentDeletedFile,
    refetchFolderContent,
    downloadFile,
  } = useFilesView()

  const Icon = variant === "vertical" ? EllipsisVertical : MoreHorizontal

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant='ghost'
          size='icon'
          className={`h-6 w-6 p-0 hover:bg-white ${triggerClassName}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Icon className='h-4 w-4 text-gray-500' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-fit p-2'>
        <div className='flex flex-col gap-2'>
          {/* Download */}
          <button
            className='flex items-center gap-2 rounded p-1 hover:bg-muted'
            onClick={(e) => {
              e.stopPropagation()
              downloadFile(item)
            }}
          >
            <div className='px-2'>
              <Download size={16} />
            </div>
            <span className='text-sm'>{translate("Download File")}</span>
          </button>

          {/* Move */}
          <button
            className='flex items-center gap-2 rounded p-1 hover:bg-muted'
            onClick={(e) => {
              e.stopPropagation()
              setIsMenuView(true)
              setSelectedMoveFile(item)
              setMoveFileModal(!moveFileModal)
            }}
          >
            <div className='flex w-full items-center gap-2'>
              <div className='px-2'>
                <FolderSearch size={16} />
              </div>
              <span className='text-sm'>{translate("Move file")}</span>
            </div>
          </button>

          {/* Cut */}
          <button
            className='flex items-center gap-2 rounded p-1 hover:bg-muted'
            onClick={(e) => e.stopPropagation()}
          >
            <CutFile
              file={item}
              folderItems={folderItems}
              activeFolder={activeFolder}
              setCutFiles={setCutFiles}
              setPreFolderItems={setPreFolderItems}
              setFolderId={setFolderId}
              setFolderItems={setFolderItems}
              isMenuView={true}
            />
          </button>

          {/* Copy */}
          <button
            className='flex items-center gap-2 rounded p-1 hover:bg-muted'
            onClick={(e) => e.stopPropagation()}
          >
            <CopyFile file={item} setCopyFiles={setCopyFiles} isMenuView={true} />
          </button>

          {/* WhatsApp */}
          <button
            className='flex items-center gap-2 rounded p-1 hover:bg-muted'
            onClick={(e) => e.stopPropagation()}
          >
            <ShareFileOnWhatsApp file={item} isMenuView={true} />
          </button>

          {/* Email */}
          <button
            className='flex items-center gap-2 rounded p-1 hover:bg-muted'
            onClick={(e) => {
              e.stopPropagation()
              setSelectedFiles([item])
              setShareViaEmailModal(true)
            }}
          >
            <div className='px-2'>
              <Mail size={14} />
            </div>
            <span className='text-sm'>{translate("Share on Mail")}</span>
          </button>

          {/* Print */}
          {PRINTABLE_EXTENSIONS.includes(item?.type) && (
            <button
              className='flex items-center gap-2 rounded p-1 hover:bg-muted'
              onClick={(e) => e.stopPropagation()}
            >
              <PrintFile file={item} isMenuView={true} />
            </button>
          )}

          {/* Restore (deleted files) */}
          {item.deleted &&
            (isOrganizationOwner ||
              isAdmin ||
              item.deleted_by === (window as any).__authConfig?.id) && (
              <button
                className='flex items-center gap-2 rounded p-1 hover:bg-muted'
                onClick={(e) => e.stopPropagation()}
              >
                <RestoreFile file={item} isMenuView={true} />
              </button>
            )}

          {/* Delete */}
          <button
            className='flex items-center gap-2 rounded p-1 hover:bg-muted'
            onClick={(e) => e.stopPropagation()}
          >
            <DeleteFile
              file={item}
              isMenuView={true}
              setFolderItems={setFolderItems}
              setBreadcrumbs={setBreadcrumbs}
              foldersMeta={foldersMeta}
              setRecentDeletedFile={setRecentDeletedFile}
              refetchFolderContent={refetchFolderContent}
            />
          </button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
